import { deleteCookie, setCookies } from '@/actions/auth';
import { APP_STATUS } from '@/constants/appStatus';
import { sendEvent } from '@/lib/firebase/analytics/sendEvent';
import { EVENTS } from '@/lib/firebase/analytics/events';
import { createUser, getUsersExists } from '../actions';
import {
  fetchSignInMethodsForEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { Dispatch, SetStateAction, useState } from 'react';
import { getAppStatus, updateLoginStats } from '@/actions/user';
import { getRedirectPathByAppStatus } from '@/utils/appStatus';
import auth from '@/lib/firebase/client';
import { PATH_AFTER_ACCOUNT_REGISTRATION } from '../constants';

export const useEmailAuth = (
  setIsLoading: Dispatch<SetStateAction<boolean>>
) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const handleEmailAuth = async () => {
    setIsLoading(true);
    const { isSuccess, message, redirectPath } =
      await authenticateFirebaseWithEmail();

    if (isSuccess) {
      location.href = redirectPath;
      return;
    }

    setIsLoading(false);
    // eslint-disable-next-line no-alert
    alert(message);
  };

  // NOTE: メールアドレスで登録もしくはログインを行う処理
  const authenticateFirebaseWithEmail = async () => {
    try {
      await deleteCookie('guestUserToken');

      // NOTE: google.comやpasswordなどの配列が返ってくる
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);

      // NOTE: すでに別の認証方法で登録されている場合
      if (signInMethods.length && !signInMethods.includes('password')) {
        return {
          isSuccess: false,
          message:
            'このメールアドレスは別の認証方法で登録されています。別の認証方法で再度お試しください。'
        } as const;
      }

      const { user } = signInMethods.length
        ? await signInWithEmailAndPassword(auth, email, password)
        : await createUserWithEmailAndPassword(auth, email, password);
      if (!user.email) {
        throw new Error('Email is null');
      }

      // NOTE: Firebase Authenticationのアクセストークンの有効期限は１時間
      //       バグ防止で実際の有効期限より10分短くする
      const fiftyMinutesMilliseconds = 60 * 50 * 1000;
      const fiftyMinutesLater = Date.now() + fiftyMinutesMilliseconds;

      await setCookies({
        accessToken: await user.getIdToken(),
        refreshToken: user.refreshToken,
        userId: user.uid,
        accessTokenExpiration: String(fiftyMinutesLater)
      });

      const userExists = await getUsersExists();

      // ビルド時に設定されるバージョンの環境変数
      const appVersion = process.env.NPM_PACKAGE_VERSION || '';

      if (!userExists) {
        const createUserResult = await createUser(
          {
            mailAddress: user.email,
            fcmToken: '',
            appStatus: APP_STATUS.accountCreated,
            webAppVersion: appVersion
          },
          await user.getIdToken()
        );

        if (!createUserResult.isSuccess) {
          throw Error(createUserResult.errorMessage);
        }

        await Promise.all([
          updateLoginStats(),
          setCookies({
            appStatus: APP_STATUS.accountCreated
          }),
          // Firebaseイベント発火
          sendEvent(EVENTS.user_account_create, {
            userId: user.uid
          })
        ]);

        return {
          isSuccess: true,
          message: '',
          redirectPath: PATH_AFTER_ACCOUNT_REGISTRATION
        } as const;
      } else {
        // NOTE: エンジニア版に登録済の場合
        const appStatus = await getAppStatus();
        if (!appStatus) throw new Error('AppStatus is null');

        await Promise.all([
          updateLoginStats(),
          setCookies({
            appStatus
          }),
          // Firebaseイベント発火
          sendEvent(EVENTS.log_in, { userId: user.uid })
        ]);

        const redirectPath = await getRedirectPathByAppStatus(appStatus);

        return {
          isSuccess: true,
          message: '',
          redirectPath
        } as const;
      }
    } catch (e) {
      return {
        isSuccess: false,
        message: 'エラーが発生しました。時間を置いてから再度お試しください。'
      } as const;
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    isPasswordVisible,
    setPasswordVisible,
    handleEmailAuth
  };
};
